import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    ArrowLeft,
    ArrowRightLeft,
    CreditCard,
    ExternalLink,
    FileText,
    Loader2,
    Search,
    User,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import { toast } from 'sonner';
import Content from '../components/layout/Content';

const paymentStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
    COMPLETED: { label: 'Abgeschlossen', variant: 'default', icon: <CheckCircle className="h-4 w-4" /> },
    PENDING: { label: 'Ausstehend', variant: 'outline', icon: <Clock className="h-4 w-4" /> },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive', icon: <XCircle className="h-4 w-4" /> },
    REFUNDED: { label: 'Erstattet', variant: 'secondary', icon: <AlertCircle className="h-4 w-4" /> },
};

const paymentTypeLabels: Record<string, string> = {
    BILLING_DRAFT_PAYMENT: 'Zahlung',
    BILLING_DRAFT_REFUND: 'Rückzahlung',
    INVOICE_PAYMENT: 'Rechnungszahlung',
};

const klarnaPaymentCategoryLabels: Record<string, string> = {
    pay_later: 'Später bezahlen (30 Tage)',
    pay_now: 'Sofort bezahlen',
    pay_in_installments: 'Ratenzahlung',
    pay_with_financing: 'Finanzierung',
};

const Payment = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const utils = trpc.useUtils();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdminOrOperator = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [moveSearchTerm, setMoveSearchTerm] = useState('');
    const [selectedTargetBillingDraftId, setSelectedTargetBillingDraftId] = useState<string | null>(null);

    // Fetch payment details
    const { data: payment, isLoading } = trpc.stripeTerminalAdmin.getPaymentById.useQuery(
        { id: id! },
        { enabled: !!id }
    );

    // Fetch Stripe details if applicable
    const { data: stripeDetails } = trpc.stripeTerminalAdmin.getPaymentStripeDetails.useQuery(
        { paymentId: id! },
        {
            enabled:
                !!id &&
                (payment?.paymentMethod === 'CARD_TERMINAL_STRIPE' ||
                    payment?.paymentMethod === 'KLARNA' ||
                    payment?.paymentMethod === 'STRIPE_CHECKOUT'),
        }
    );

    const { data: searchResults } = trpc.payments.searchBillingDraftsForCustomer.useQuery(
        { customerId: payment?.customer.id ?? 0, searchTerm: moveSearchTerm },
        { enabled: showMoveDialog && !!payment?.customer.id && moveSearchTerm.length > 0 }
    );

    const movePaymentMutation = trpc.payments.movePaymentToBillingDraft.useMutation({
        onSuccess: () => {
            toast.success('Zahlung wurde verschoben');
            setShowMoveDialog(false);
            setMoveSearchTerm('');
            setSelectedTargetBillingDraftId(null);
            utils.stripeTerminalAdmin.getPaymentById.invalidate({ id: id! });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    if (isLoading) {
        return (
            <Content>
                <div className="container mx-auto py-10 max-w-4xl">
                    <Skeleton className="h-8 w-48 mb-6" />
                    <div className="grid gap-6">
                        <Skeleton className="h-48" />
                        <Skeleton className="h-32" />
                        <Skeleton className="h-32" />
                    </div>
                </div>
            </Content>
        );
    }

    if (!payment) {
        return (
            <Content>
                <div className="container mx-auto py-10">
                    <div className="text-center py-12">
                        <h2 className="text-xl font-semibold mb-2">Zahlung nicht gefunden</h2>
                        <Button variant="outline" onClick={() => navigate('/payments')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Zurück zu Zahlungen
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    const methodConfig = paymentMethodConfig[payment.paymentMethod || ''] || defaultPaymentMethod;
    const statusConfig = paymentStatusConfig[payment.status] || { label: payment.status, variant: 'outline' as const, icon: null };
    const isRefund = payment.type === 'BILLING_DRAFT_REFUND';
    const amountFormatted = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(payment.paidAmountCents / 100);

    return (
        <Content>
            <div className="container mx-auto py-10 max-w-4xl">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/payments')}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold">{isRefund ? 'Rückzahlung' : 'Zahlung'}</h1>
                            <Badge variant={statusConfig.variant} className="flex items-center gap-1">
                                {statusConfig.icon}
                                {statusConfig.label}
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground font-mono">{payment.id}</p>
                    </div>
                    <div className="text-right">
                        <div className={`text-2xl font-bold ${isRefund ? 'text-destructive' : ''}`}>{amountFormatted}</div>
                        <div className="text-sm text-muted-foreground">
                            {format(new Date(payment.createdAt), "dd. MMMM yyyy 'um' HH:mm", { locale: de })}
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {/* Payment Details */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="h-5 w-5" />
                                Zahlungsdetails
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Betrag</span>
                                <span className="font-medium">{amountFormatted}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Typ</span>
                                <span>{paymentTypeLabels[payment.type] || payment.type}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Zahlungsmethode</span>
                                <span className="flex items-center gap-2">
                                    <methodConfig.icon className="h-5 w-5" />
                                    {methodConfig.label}
                                </span>
                            </div>
                            {payment.paymentMethod === 'KLARNA' && payment.klarnaPaymentCategory && (
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Klarna-Zahlungsart</span>
                                    <span>{klarnaPaymentCategoryLabels[payment.klarnaPaymentCategory] ?? payment.klarnaPaymentCategory}</span>
                                </div>
                            )}
                            {payment.paymentMethod === 'GIFT_CARD' && payment.giftCardTransaction?.giftCard?.giftCardNumber && (
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Gutscheinnr.</span>
                                    <span className="font-mono text-sm">{payment.giftCardTransaction.giftCard.giftCardNumber}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Status</span>
                                <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                            </div>
                            {payment.externalReference && (
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Externe Referenz</span>
                                    <span className="font-mono text-sm">{payment.externalReference}</span>
                                </div>
                            )}
                            {payment.note && (
                                <div className="pt-2 border-t">
                                    <span className="text-muted-foreground text-sm">Notiz</span>
                                    <p className="mt-1">{payment.note}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Customer */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Kunde
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Name</span>
                                <span 
                                    className="font-medium cursor-pointer hover:underline"
                                    onClick={() => navigate(`/customer/${payment.customer.id}`)}
                                >
                                    {payment.customer.firstName} {payment.customer.lastName}
                                </span>
                            </div>
                            {payment.customer.email && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">E-Mail</span>
                                    <span>{payment.customer.email}</span>
                                </div>
                            )}
                            {payment.customer.phone && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Telefon</span>
                                    <span>{payment.customer.phone}</span>
                                </div>
                            )}
                            <Button 
                                variant="outline" 
                                className="w-full mt-2"
                                onClick={() => navigate(`/customer/${payment.customer.id}`)}
                            >
                                Kundenprofil öffnen
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Billing Draft (if linked) */}
                    {payment.billingDraft && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Auftrag
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Entwurfsnr.</span>
                                    <span 
                                        className="font-mono cursor-pointer hover:underline"
                                        onClick={() => navigate(`/billing-draft/${payment.billingDraft!.id}`)}
                                    >
                                        {payment.billingDraft.billingDraftNumber}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Entwurfsdatum</span>
                                    <span>
                                        {format(new Date(payment.billingDraft.billingDraftDate), 'dd.MM.yyyy', { locale: de })}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Entwurfsbetrag</span>
                                    <span className="font-medium">
                                        {new Intl.NumberFormat('de-DE', {
                                            style: 'currency',
                                            currency: 'EUR',
                                        }).format(Number(payment.billingDraft.totalGrossDiscounted))}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Filiale</span>
                                    <span>{payment.billingDraft.store.name}</span>
                                </div>
                                <Button 
                                    variant="outline" 
                                    className="w-full mt-2"
                                    onClick={() => navigate(`/billing-draft/${payment.billingDraft!.id}`)}
                                >
                                    Auftrag öffnen
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stripe Details (terminal, Klarna, or payment link) */}
                    {stripeDetails && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CreditCard className="h-5 w-5" />
                                    Stripe-Details
                                </CardTitle>
                                <CardDescription>
                                    {payment.paymentMethod === 'KLARNA'
                                        ? 'Klarna-Zahlungsinformationen'
                                        : payment.paymentMethod === 'STRIPE_CHECKOUT'
                                            ? 'Stripe Zahlungslink-Informationen'
                                            : 'Terminal-Zahlungsinformationen'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">PaymentIntent ID</span>
                                    <a 
                                        href={`https://dashboard.stripe.com/payments/${stripeDetails.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-sm flex items-center gap-1 hover:underline text-primary"
                                    >
                                        {stripeDetails.id.substring(0, 20)}...
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                                {stripeDetails.paymentMethodType && !stripeDetails.cardBrand && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Zahlungsart</span>
                                        <span className="capitalize">{stripeDetails.paymentMethodType}</span>
                                    </div>
                                )}
                                {stripeDetails.cardBrand && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Karte</span>
                                        <span className="capitalize">
                                            {stripeDetails.cardBrand} •••• {stripeDetails.cardLast4}
                                        </span>
                                    </div>
                                )}
                                {stripeDetails.cardExpMonth && stripeDetails.cardExpYear && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Gültig bis</span>
                                        <span>
                                            {String(stripeDetails.cardExpMonth).padStart(2, '0')}/{stripeDetails.cardExpYear}
                                        </span>
                                    </div>
                                )}
                                {stripeDetails.receiptUrl && (
                                    <Button 
                                        variant="outline" 
                                        className="w-full mt-2"
                                        asChild
                                    >
                                        <a 
                                            href={stripeDetails.receiptUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ExternalLink className="mr-2 h-4 w-4" />
                                            Beleg anzeigen
                                        </a>
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Audit Trail */}
                    <Card className={stripeDetails ? '' : 'md:col-span-2'}>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="h-5 w-5" />
                                Protokoll
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Erstellt am</span>
                                <span>
                                    {format(new Date(payment.createdAt), "dd.MM.yyyy 'um' HH:mm:ss", { locale: de })}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Erstellt von</span>
                                <span>
                                    {payment.createdByUser 
                                        ? `${payment.createdByUser.firstName} ${payment.createdByUser.lastName}`
                                        : 'System'
                                    }
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Admin Actions */}
                {isAdminOrOperator && payment.billingDraft && (
                    <div className="flex gap-3 mt-6">
                        <Button
                            variant="outline"
                            disabled={payment.paymentMethod === 'BUNDLE_VOUCHER'}
                            onClick={() => {
                                setShowMoveDialog(true);
                                setMoveSearchTerm('');
                                setSelectedTargetBillingDraftId(null);
                            }}
                        >
                            <ArrowRightLeft className="mr-2 h-4 w-4" />
                            Zahlung verschieben
                        </Button>
                    </div>
                )}

                {/* Move Payment Dialog */}
                <Dialog open={showMoveDialog} onOpenChange={(open) => {
                    setShowMoveDialog(open);
                    if (!open) {
                        setMoveSearchTerm('');
                        setSelectedTargetBillingDraftId(null);
                    }
                }}>
                    <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                            <DialogTitle>Zahlung verschieben</DialogTitle>
                            <DialogDescription>
                                Wähle den Zielauftrag, zu dem die Zahlung über {amountFormatted} verschoben werden soll.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Auftragsnummer suchen..."
                                    value={moveSearchTerm}
                                    onChange={(e) => {
                                        setMoveSearchTerm(e.target.value);
                                        setSelectedTargetBillingDraftId(null);
                                    }}
                                    className="pl-10"
                                />
                            </div>
                            {moveSearchTerm.length > 0 && searchResults && (
                                <div className="border rounded-md max-h-[200px] overflow-y-auto">
                                    {searchResults.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground text-center">
                                            Keine Aufträge gefunden
                                        </div>
                                    ) : (
                                        searchResults
                                            .filter(bd => bd.id !== payment.billingDraft?.id)
                                            .map((bd) => (
                                                <div
                                                    key={bd.id}
                                                    className={`flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 border-b last:border-b-0 ${
                                                        selectedTargetBillingDraftId === bd.id ? 'bg-muted' : ''
                                                    }`}
                                                    onClick={() => setSelectedTargetBillingDraftId(bd.id)}
                                                >
                                                    <div>
                                                        <div className="font-mono text-sm font-medium">{bd.billingDraftNumber}</div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {format(new Date(bd.billingDraftDate), 'dd.MM.yyyy', { locale: de })}
                                                            {' · '}
                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(bd.totalGrossDiscounted)}
                                                        </div>
                                                    </div>
                                                    <Badge variant={bd.status === 'PAID' ? 'default' : bd.status === 'CANCELLED' ? 'destructive' : 'outline'}>
                                                        {bd.status === 'PAID' ? 'Bezahlt' : bd.status === 'CANCELLED' ? 'Storniert' : 'Ausstehend'}
                                                    </Badge>
                                                </div>
                                            ))
                                    )}
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                disabled={!selectedTargetBillingDraftId || movePaymentMutation.isPending}
                                onClick={() => {
                                    if (selectedTargetBillingDraftId) {
                                        movePaymentMutation.mutate({
                                            paymentId: payment.id,
                                            targetBillingDraftId: selectedTargetBillingDraftId,
                                        });
                                    }
                                }}
                            >
                                {movePaymentMutation.isPending ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verschieben...</>
                                ) : (
                                    'Verschieben'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Content>
    );
};

export default Payment;
